"use client";

import React from "react";

const FIT_EPSILON_PX = 0.5;
const FIT_PRECISION_PX = 0.25;

export default function AutoFitHeaderTitle({
  text = "",
  className = "",
  wrapperClassName = "",
  minFontSize = 14,
  maxFontSize = 24,
  truncate = false,
}) {
  const containerRef = React.useRef(null);
  const textRef = React.useRef(null);
  const [fontSize, setFontSize] = React.useState(maxFontSize);

  const measure = React.useCallback(() => {
    const container = containerRef.current;
    const label = textRef.current;
    if (!container || !label) return;

    if (truncate) {
      setFontSize((prev) => (prev === maxFontSize ? prev : maxFontSize));
      return;
    }

    const availableWidth = container.clientWidth;
    if (!availableWidth) return;

    const fitsAt = (size) => {
      label.style.fontSize = `${size}px`;
      return label.scrollWidth <= availableWidth + FIT_EPSILON_PX;
    };

    if (!text) {
      setFontSize(maxFontSize);
      return;
    }

    if (fitsAt(maxFontSize)) {
      setFontSize((prev) => (Math.abs(prev - maxFontSize) <= FIT_PRECISION_PX ? prev : maxFontSize));
      return;
    }

    let low = minFontSize;
    let high = maxFontSize;
    let best = minFontSize;

    while (high - low > FIT_PRECISION_PX) {
      const mid = Number(((low + high) / 2).toFixed(2));
      if (fitsAt(mid)) {
        best = mid;
        low = mid;
      } else {
        high = mid;
      }
    }

    const resolvedFontSize = Math.max(
      minFontSize,
      Math.min(maxFontSize, Number(best.toFixed(2)))
    );
    setFontSize((prev) =>
      Math.abs(prev - resolvedFontSize) <= FIT_PRECISION_PX ? prev : resolvedFontSize
    );
  }, [maxFontSize, minFontSize, text, truncate]);

  React.useLayoutEffect(() => {
    measure();
  }, [measure]);

  React.useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const scheduleMeasure = () => {
      window.requestAnimationFrame(() => {
        measure();
      });
    };

    const container = containerRef.current;
    const resizeObserver =
      typeof ResizeObserver !== "undefined" && container
        ? new ResizeObserver(scheduleMeasure)
        : null;

    if (container && resizeObserver) {
      resizeObserver.observe(container);
    }

    window.addEventListener("resize", scheduleMeasure);

    if (document?.fonts?.ready) {
      document.fonts.ready.then(scheduleMeasure).catch(() => {});
    }

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener("resize", scheduleMeasure);
    };
  }, [measure]);

  return (
    <span
      ref={containerRef}
      className={`block max-w-full ${truncate ? "overflow-hidden" : ""} ${wrapperClassName}`.trim()}
    >
      <span
        ref={textRef}
        className={`block whitespace-nowrap ${truncate ? "overflow-hidden text-ellipsis" : ""} ${className}`.trim()}
        style={{ fontSize: `${fontSize}px` }}
      >
        {text}
      </span>
    </span>
  );
}
